import { helpWhy } from '../../help/why';
import React, { useEffect, useState } from 'react';
import {
    Button, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, InputLabel, MenuItem, Select, Typography,
} from '@mui/material';
import { AuthService } from '../../services/auth.service';
import { useToast } from '../../context/ToastContext';
import { ExportLcService } from '../../services/exportLc.service';
import { ExportLc, LcStatus } from '../../interfaces/commercial.interface';
import { enumLabel } from '../../utils/garmentsLabels';

/** The moves the server allows from each LC status (a step back is allowed to correct a slip). */
export const LC_STATUS_TRANSITIONS: Record<LcStatus, LcStatus[]> = {
    RECEIVED: ['PARTIALLY_SHIPPED', 'FULLY_SHIPPED', 'CLOSED'],
    PARTIALLY_SHIPPED: ['RECEIVED', 'FULLY_SHIPPED', 'CLOSED'],
    FULLY_SHIPPED: ['PARTIALLY_SHIPPED', 'REALIZED', 'CLOSED'],
    REALIZED: ['FULLY_SHIPPED', 'CLOSED'],
    CLOSED: [],
};

interface Props {
    open: boolean;
    lc: ExportLc;
    onClose: () => void;
    onUpdated: (lc: ExportLc) => void;
}

/** Moves an export LC to its next status, offering only the moves the server allows. */
const LcStatusDialog: React.FC<Props> = ({ open, lc, onClose, onUpdated }) => {
    const toast = useToast();
    const options = LC_STATUS_TRANSITIONS[lc.status] ?? [];
    const [target, setTarget] = useState<LcStatus | ''>('');
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (open) setTarget(options[0] ?? '');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, lc.id, lc.status]);

    const saveWhy = saving ? null
        : !target ? 'Pick the new status.'
        : null;

    const submit = async () => {
        const institutionId = AuthService.getSelectedInstitution();
        if (!institutionId || !target) return;
        setSaving(true);
        try {
            const updated = await ExportLcService.updateStatus(institutionId, lc.id, { status: target });
            toast.showSuccess(`LC status changed to ${enumLabel(target)}`);
            onUpdated(updated);
            onClose();
        } catch (e) {
            toast.showApiError(e, 'Failed to change LC status');
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth PaperProps={{ 'data-help-page': 'commercial.lcStatus.page' } as any}>
            <DialogTitle>Change LC status</DialogTitle>
            <DialogContent data-help-section="commercial.lcStatus.section.status">
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Current status: {enumLabel(lc.status)}
                </Typography>
                <FormControl data-help="commercial.lcStatus.field.target" fullWidth size="small" disabled={saving || options.length === 0}>
                    <InputLabel id="lc-status-target-label">New status</InputLabel>
                    <Select
                        labelId="lc-status-target-label" label="New status" value={target}
                        onChange={e => setTarget(e.target.value as LcStatus)}
                    >
                        {options.map(s => <MenuItem key={s} value={s}>{enumLabel(s)}</MenuItem>)}
                    </Select>
                </FormControl>
            </DialogContent>
            <DialogActions>
                <Button data-help="commercial.lcStatus.cancel" onClick={onClose} disabled={saving}>Cancel</Button>
                <Button data-help="commercial.lcStatus.submit" {...helpWhy(saveWhy ? { kind: 'status', message: saveWhy } : null)} variant="contained" onClick={submit} disabled={saving || !target}>Update</Button>
            </DialogActions>
        </Dialog>
    );
};

export default LcStatusDialog;
